import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig, apiScopes } from "./authConfig";

export const SESSION_ENDED_EVENT = "wolfwalker:session-ended";

export class SessionEndedError extends Error {
  constructor(reason) {
    super("Session ended");
    this.name = "SessionEndedError";
    this.reason = reason;
  }
}

function needsInteraction(error) {
  const code = error?.errorCode || "";
  const msg = error?.message || "";
  return (
    code === "consent_required" ||
    code === "interaction_required" ||
    msg.includes("consent_required") ||
    msg.includes("interaction_required")
  );
}

function isPopupUserCancellation(error) {
  const code = error?.errorCode || "";
  return code === "user_cancelled" || code === "user_canceled";
}

const SESSION_ENDING_MSAL_CODES = new Set([
  "login_required",
  "token_expired",
  "no_tokens_found",
  "no_account_in_silent_request",
  "monitor_window_timeout",
  "invalid_grant",
  "client_info_decoding_error",
]);

class AuthService {
  constructor() {
    this.msalInstance = new PublicClientApplication(msalConfig);
    this.account = null;
    this._sessionEndedListeners = new Set();
    this._endingSession = false;
  }

  onSessionEnded(callback) {
    this._sessionEndedListeners.add(callback);
    return () => this._sessionEndedListeners.delete(callback);
  }

  _notifySessionEnded(reason) {
    for (const listener of this._sessionEndedListeners) {
      try {
        listener(reason);
      } catch (e) {
        console.error("Session ended listener error:", e);
      }
    }
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent(SESSION_ENDED_EVENT, { detail: { reason } })
      );
    }
  }

  isSessionEndedError(error) {
    return error instanceof SessionEndedError;
  }

  ensureLoggedIn() {
    if (!this.account) {
      throw new Error("No account logged in");
    }
  }

  /**
   * Clear local MSAL session and notify UI (no Azure AD logout popup).
   */
  async endLocalSession(reason, { throwError = false } = {}) {
    if (this._endingSession) {
      if (throwError) {
        throw new SessionEndedError(reason);
      }
      return;
    }

    this._endingSession = true;
    try {
      this.account = null;
      try {
        await this.msalInstance.clearCache();
      } catch (clearError) {
        console.warn("MSAL clearCache failed:", clearError);
      }
      this._notifySessionEnded(reason);
    } finally {
      this._endingSession = false;
    }

    if (throwError) {
      throw new SessionEndedError(reason);
    }
  }

  async _acquireTokenForScopes(scopes) {
    this.ensureLoggedIn();

    try {
      const tokenResponse = await this.msalInstance.acquireTokenSilent({
        scopes,
        account: this.account,
      });
      return tokenResponse.accessToken;
    } catch (error) {
      if (needsInteraction(error)) {
        try {
          const tokenResponse = await this.msalInstance.acquireTokenPopup({
            scopes,
            account: this.account,
          });
          return tokenResponse.accessToken;
        } catch (popupError) {
          if (isPopupUserCancellation(popupError)) {
            throw popupError;
          }
          console.error("Interactive token acquisition failed:", popupError);
          await this.endLocalSession("token_renewal_failed", { throwError: true });
        }
      }

      const code = error?.errorCode || "";
      if (SESSION_ENDING_MSAL_CODES.has(code)) {
        console.error("Token acquisition failed (session):", error);
        await this.endLocalSession(code, { throwError: true });
      }

      console.error("Token acquisition failed:", error);
      await this.endLocalSession("token_acquisition_failed", { throwError: true });
    }
  }

  async initialize() {
    await this.msalInstance.initialize();
    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      this.account = accounts[0];
    }
  }

  async login() {
    try {
      const loginResponse = await this.msalInstance.loginPopup({
        scopes: ["User.Read"],
        prompt: "select_account",
      });
      this.account = loginResponse.account;
      return loginResponse;
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  }

  async logout() {
    await this.msalInstance.logoutPopup({
      account: this.account,
    });
    this.account = null;
  }

  async getAccessToken(scopeType = "qgisApi") {
    if (!this.account) {
      console.error("No account logged in");
      throw new Error("No account logged in");
    }

    const scopes = apiScopes[scopeType]?.scopes || apiScopes.qgisApi.scopes;
    return this._acquireTokenForScopes(scopes);
  }

  isLoggedIn() {
    return this.account !== null;
  }

  getUserInfo() {
    return this.account
      ? {
          name: this.account.name,
          username: this.account.username,
          email: this.account.username,
          id: this.account.homeAccountId,
        }
      : null;
  }

  getUserId() {
    return this.account ? this.account.homeAccountId : null;
  }

  /**
   * Access token for Microsoft Graph directory reads (User.ReadBasic.All).
   */
  async getGraphDirectoryToken() {
    if (!this.account) {
      throw new Error("No account logged in");
    }

    const scopes = apiScopes.directoryRead?.scopes || ["User.ReadBasic.All"];
    return this._acquireTokenForScopes(scopes);
  }

  async getUserPhoto() {
    if (!this.account) {
      return null;
    }

    try {
      const accessToken = await this._acquireTokenForScopes(["User.Read"]);

      const headers = {
        Authorization: `Bearer ${accessToken}`,
      };

      const photoResponse = await fetch(
        "https://graph.microsoft.com/v1.0/me/photo/$value",
        { headers }
      );

      if (!photoResponse.ok) {
        if (photoResponse.status === 404) {
          return null;
        }
        if (photoResponse.status === 401 || photoResponse.status === 403) {
          await this.endLocalSession("graph_photo_unauthorized", {
            throwError: true,
          });
        }
        throw new Error(`Graph API photo error: ${photoResponse.status}`);
      }

      const photoBlob = await photoResponse.blob();
      return URL.createObjectURL(photoBlob);
    } catch (error) {
      if (this.isSessionEndedError(error) || isPopupUserCancellation(error)) {
        throw error;
      }
      console.error("Error getting user photo:", error);
      return null;
    }
  }

  async _fetchGraphGroups(accessToken) {
    const headers = {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    };

    const membershipResponse = await fetch(
      "https://graph.microsoft.com/v1.0/me/memberOf",
      { headers }
    );

    if (!membershipResponse.ok) {
      if (
        membershipResponse.status === 401 ||
        membershipResponse.status === 403
      ) {
        await this.endLocalSession("graph_groups_unauthorized", {
          throwError: true,
        });
      }
      throw new Error(
        `Graph API memberOf error: ${membershipResponse.status}`
      );
    }

    const membershipData = await membershipResponse.json();

    const ownershipResponse = await fetch(
      "https://graph.microsoft.com/v1.0/me/ownedObjects",
      { headers }
    );

    if (!ownershipResponse.ok) {
      if (
        ownershipResponse.status === 401 ||
        ownershipResponse.status === 403
      ) {
        await this.endLocalSession("graph_groups_unauthorized", {
          throwError: true,
        });
      }
      throw new Error(
        `Graph API ownedObjects error: ${ownershipResponse.status}`
      );
    }

    const ownershipData = await ownershipResponse.json();

    const memberGroups = membershipData.value
      .filter((item) => item["@odata.type"] === "#microsoft.graph.group")
      .map((group) => group.id);

    const ownedGroups = ownershipData.value
      .filter((item) => item["@odata.type"] === "#microsoft.graph.group")
      .map((group) => group.id);

    return [...new Set([...memberGroups, ...ownedGroups])];
  }

  /**
   * Get user's group memberships and ownerships using Microsoft Graph API
   */
  async getUserGroups() {
    if (!this.account) {
      return [];
    }

    const accessToken = await this._acquireTokenForScopes(
      apiScopes.groupAccess.scopes
    );
    return this._fetchGraphGroups(accessToken);
  }

  async isUserInGroup(groupId) {
    const userGroups = await this.getUserGroups();
    return userGroups.includes(groupId);
  }
}

export const authService = new AuthService();
